import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, IconButton, Tooltip, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, ContentCopy as CopyIcon, Block as BlockIcon, Edit as EditIcon, Check as CheckIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

interface JsonDiffViewerProps {
  currentData: any;
  incomingData: any;
  recordIndex: number;
  recordType?: string;
  onDataChange?: (field: string, value: any) => void;
  editable?: boolean;
  showOnlyConflicts?: boolean;
}

const JsonDiffViewer: React.FC<JsonDiffViewerProps> = ({
  currentData,
  incomingData,
  recordIndex,
  recordType = 'Record',
  onDataChange,
  editable = false,
  showOnlyConflicts = false
}) => {
  console.log('JsonDiffViewer rendered with editable:', editable, 'onDataChange:', !!onDataChange, 'currentData:', currentData, 'incomingData:', incomingData);
  
  // Helper function to normalize boolean strings in data
  const normalizeDataBooleans = (data: any): any => {
    if (data === null || data === undefined) return data;
    if (Array.isArray(data)) {
      return data.map(item => normalizeDataBooleans(item));
    }
    if (typeof data === 'object') {
      const normalized: any = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          const value = data[key];
          if (typeof value === 'string') {
            let trimmed = value.trim();
            // Remove surrounding quotes if present
            if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
                (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
              trimmed = trimmed.slice(1, -1).trim();
            }
            const lowerTrimmed = trimmed.toLowerCase();
            if (lowerTrimmed === 'true' || lowerTrimmed === '1' || lowerTrimmed === 'yes') {
              normalized[key] = true;
            } else if (lowerTrimmed === 'false' || lowerTrimmed === '0' || lowerTrimmed === 'no') {
              normalized[key] = false;
            } else {
              normalized[key] = normalizeDataBooleans(value);
            }
          } else {
            normalized[key] = normalizeDataBooleans(value);
          }
        }
      }
      return normalized;
    }
    return data;
  };
  
  // Normalize boolean strings in current and incoming data
  const normalizedCurrentData = React.useMemo(() => normalizeDataBooleans(currentData), [currentData]);
  const normalizedIncomingData = React.useMemo(() => normalizeDataBooleans(incomingData), [incomingData]);
  
  // Local state for incoming data
  const [localIncomingData, setLocalIncomingData] = useState(normalizedIncomingData);
  // State for tracking which fields are being edited
  const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
  // State for tracking temporary edit values
  const [tempEditValues, setTempEditValues] = useState<Record<string, any>>({});
  const [isExpanded, setIsExpanded] = useState(true);
  // Refs for input fields
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // Track which fields are currently being saved to prevent double saves
  const savingFields = useRef<Set<string>>(new Set());
  // Track recently started editing to prevent immediate blur
  const recentlyStartedEditing = useRef<Set<string>>(new Set());

  // Update local data when incoming data changes
  React.useEffect(() => {
    setLocalIncomingData(normalizedIncomingData);
  }, [normalizedIncomingData]);

  // Helper function to start editing a field
  const getCursorPositionFromClick = (
    event: React.MouseEvent<HTMLElement>,
    textValue: string
  ): number | null => {
    const docWithCaret = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (x: number, y: number) => { offset: number } | null;
    };

    let offset: number | null = null;

    if (docWithCaret.caretRangeFromPoint) {
      const range = docWithCaret.caretRangeFromPoint(event.clientX, event.clientY);
      if (range && typeof range.startOffset === 'number') {
        offset = range.startOffset;
      }
    } else if (docWithCaret.caretPositionFromPoint) {
      const caretPos = docWithCaret.caretPositionFromPoint(event.clientX, event.clientY);
      if (caretPos && typeof caretPos.offset === 'number') {
        offset = caretPos.offset;
      }
    }

    if (offset === null || Number.isNaN(offset)) {
      return null;
    }

    return Math.max(0, Math.min(offset, textValue.length));
  };

  const startEditing = (field: string, currentValue: any, cursorPosition?: number | null) => {
    setEditingFields(prev => new Set(prev).add(field));
    setTempEditValues(prev => ({
      ...prev,
      [field]: formatValueForEdit(currentValue)
    }));
    
    // Mark as recently started to prevent immediate blur
    recentlyStartedEditing.current.add(field);
    setTimeout(() => {
      recentlyStartedEditing.current.delete(field);
    }, 300); // Remove after 300ms
    
    // Focus the input immediately without selecting all text
    setTimeout(() => {
      const inputElement = inputRefs.current[field];
      if (inputElement) {
        const input = inputElement.querySelector('input') || inputElement.querySelector('textarea');
        if (input) {
          input.focus();
          if (typeof cursorPosition === 'number') {
            const clamped = Math.max(0, Math.min(cursorPosition, input.value.length));
            input.setSelectionRange(clamped, clamped);
          }
        } else {
          inputElement.focus();
        }
      }
    }, 10);
  };

  // Helper function to cancel editing
  const cancelEditing = (field: string) => {
    setEditingFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(field);
      return newSet;
    });
    setTempEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
  };

  // Helper function to save editing
  const saveEditing = (field: string) => {
    // Prevent double saves
    if (savingFields.current.has(field)) {
      return;
    }
    savingFields.current.add(field);
    
    try {
      const tempValue = tempEditValues[field];
      const originalIncomingValue = normalizedIncomingData?.[field];
      
      // Determine what value to save
      let valueToSave = originalIncomingValue;
      
      if (tempValue !== undefined && tempValue !== null && tempValue !== '') {
        const parsedValue = parseValueFromEdit(tempValue, originalIncomingValue);
        const originalFormatted = formatValueForEdit(originalIncomingValue);
        
        // Only use new value if it actually changed
        if (originalFormatted.trim() !== tempValue.trim()) {
          valueToSave = parsedValue;
        }
      }
      
      // Always ensure field exists in localIncomingData (preserve original if no changes)
      if (valueToSave !== undefined) {
        setLocalIncomingData(prev => {
          // Always set the value to ensure field exists
          return {
            ...prev,
            [field]: valueToSave
          };
        });

        // Call parent callback if value changed
        if (onDataChange) {
          const currentValue = localIncomingData?.[field];
          if (valueToSave !== currentValue) {
            onDataChange(field, valueToSave);
          }
        }
      } else if (originalIncomingValue !== undefined) {
        // Preserve original if tempValue was empty
        setLocalIncomingData(prev => ({
          ...prev,
          [field]: originalIncomingValue
        }));
      }
    } finally {
      // Always stop editing and clear temp values
      setEditingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(field);
        return newSet;
      });
      setTempEditValues(prev => {
        const newValues = { ...prev };
        delete newValues[field];
        return newValues;
      });
      savingFields.current.delete(field);
    }
  };

  // Helper function to format value for editing
  const formatValueForEdit = (value: any) => {
    if (value === null) return 'null';
    if (value === undefined) return ''; // Return empty string instead of 'undefined'
    if (typeof value === 'string') return value; // Don't add quotes for editing
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'number') return value.toString();
    if (Array.isArray(value)) return JSON.stringify(value, null, 2);
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Helper function to parse value from edit
  const parseValueFromEdit = (value: string, originalValue?: any) => {
    const trimmed = value.trim();

    if (trimmed === 'null') return null;
    if (trimmed === 'undefined') return undefined;
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;

    const tryParseJson = (candidate: string): any => {
      try {
        return JSON.parse(candidate);
      } catch {
        return null;
      }
    };

    // Preserve object/array type when original value was structured.
    const originalIsStructured =
      Array.isArray(originalValue) ||
      (typeof originalValue === 'object' && originalValue !== null);

    if (originalIsStructured) {
      const directParsed = tryParseJson(trimmed);
      if (directParsed !== null) return directParsed;

      // Try one-level unwrap for quoted JSON payloads.
      const unwrapped = tryParseJson(trimmed);
      if (typeof unwrapped === 'string') {
        const nestedParsed = tryParseJson(unwrapped.trim());
        if (nestedParsed !== null) return nestedParsed;
      }

      // Try extracting JSON block from noisy wrapper.
      const firstObjectStart = trimmed.indexOf('{');
      const lastObjectEnd = trimmed.lastIndexOf('}');
      if (firstObjectStart !== -1 && lastObjectEnd > firstObjectStart) {
        const objectSlice = trimmed.slice(firstObjectStart, lastObjectEnd + 1);
        const parsedObjectSlice = tryParseJson(objectSlice);
        if (parsedObjectSlice !== null) return parsedObjectSlice;
      }

      const firstArrayStart = trimmed.indexOf('[');
      const lastArrayEnd = trimmed.lastIndexOf(']');
      if (firstArrayStart !== -1 && lastArrayEnd > firstArrayStart) {
        const arraySlice = trimmed.slice(firstArrayStart, lastArrayEnd + 1);
        const parsedArraySlice = tryParseJson(arraySlice);
        if (parsedArraySlice !== null) return parsedArraySlice;
      }

      // If parsing fails during intermediate editing, keep user input as text.
      // Final payload normalization/conversion is handled at parent level.
      return value;
    }

    // Try to parse as number for primitive fields
    if (!Number.isNaN(Number(trimmed)) && trimmed !== '') {
      return Number(trimmed);
    }

    // Try regular JSON parse for primitive fields (e.g. booleans/arrays typed intentionally)
    const parsed = tryParseJson(trimmed);
    if (parsed !== null) return parsed;

    // Default: plain string
    return value;
  };

  // Function to create a unified field order for both objects
  const createUnifiedFieldOrder = (obj1: any, obj2: any) => {
    const allFields = new Set<string>();
    if (obj1) Object.keys(obj1).forEach(key => allFields.add(key));
    if (obj2) Object.keys(obj2).forEach(key => allFields.add(key));
    return Array.from(allFields).sort();
  };

  // Helper function to check if value is empty (empty string, null, undefined)
  const isEmptyValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    // Check for empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    // Check for empty objects
    if (typeof value === 'object' && Object.keys(value).length === 0) {
      return true;
    }
    return false;
  };

  // Function to compare two values and determine the change type
  // Ignores empty/null/undefined values - they are not considered in comparison
  const getChangeType = (currentValue: any, incomingValue: any) => {
    // If both are empty, consider unchanged (don't show)
    if (isEmptyValue(currentValue) && isEmptyValue(incomingValue)) {
      return 'unchanged';
    }
    
    // If incoming is empty, don't show (not a change)
    if (isEmptyValue(incomingValue)) {
      return 'unchanged';
    }
    
    // If current is empty but incoming has value, it's added
    if (isEmptyValue(currentValue) && !isEmptyValue(incomingValue)) {
      return 'added';
    }
    
    // If current has value but incoming is empty, don't show (not removing, just ignoring empty)
    if (!isEmptyValue(currentValue) && isEmptyValue(incomingValue)) {
      return 'unchanged';
    }
    
    // Both have values - check if they're different
    if (JSON.stringify(currentValue) !== JSON.stringify(incomingValue)) {
      return 'modified';
    }
    
    return 'unchanged';
  };

  // Function to find differences between two strings and return highlighted JSX
  // For red line (isRemoved=true): shows oldStr with removed parts highlighted
  // For green line (isRemoved=false): shows newStr with added parts highlighted
  const highlightStringDiff = (oldStr: string, newStr: string, isRemoved: boolean = false) => {
    if (oldStr === newStr) {
      return <span>{isRemoved ? oldStr : newStr}</span>;
    }

    // For removed line: show old string, highlight what's different
    // For added line: show new string, highlight what's different
    const displayStr = isRemoved ? oldStr : newStr;
    const compareStr = isRemoved ? newStr : oldStr;

    // Simple word-level diff algorithm
    const displayWords = displayStr.split(/(\s+)/);
    const compareWords = compareStr.split(/(\s+)/);
    
    // Find which words/parts are different
    const diff: Array<{ type: 'different' | 'common'; text: string | React.ReactNode; index: number; isCharDiff?: boolean }> = [];
    
    let displayIdx = 0;
    let compareIdx = 0;
    
    while (displayIdx < displayWords.length) {
      if (compareIdx >= compareWords.length) {
        // No more compare words - all remaining are different
        diff.push({ type: 'different', text: displayWords[displayIdx], index: displayIdx });
        displayIdx++;
      } else if (displayWords[displayIdx] === compareWords[compareIdx]) {
        // Words match
        diff.push({ type: 'common', text: displayWords[displayIdx], index: displayIdx });
        displayIdx++;
        compareIdx++;
      } else {
        // Words don't match - check if it's a small difference (character-level)
        if (displayWords[displayIdx].length < 50 && compareWords[compareIdx] && compareWords[compareIdx].length < 50) {
          // Character-level diff for short strings
          const charDiff = highlightCharDiffForDisplay(displayWords[displayIdx], compareWords[compareIdx], isRemoved);
          diff.push({ type: 'different', text: charDiff, index: displayIdx, isCharDiff: true });
        } else {
          // Word is different
          diff.push({ type: 'different', text: displayWords[displayIdx], index: displayIdx });
        }
        displayIdx++;
        compareIdx++;
      }
    }
    
    // Render the diff
    return (
      <span>
        {diff.map((item, idx) => {
          if (item.type === 'common') {
            return <span key={idx}>{item.text}</span>;
          } else if (item.isCharDiff) {
            // Character-level diff already returns JSX
            return <span key={idx}>{item.text}</span>;
          } else {
            // Different part - highlight it
            return (
              <span
                key={idx}
                style={{
                  backgroundColor: isRemoved ? '#ffd7d9' : '#acf2bd',
                  color: isRemoved ? '#d73a49' : '#28a745',
                  fontWeight: 500
                }}
              >
                {item.text}
              </span>
            );
          }
        })}
      </span>
    );
  };

  // Character-level diff for short strings - returns JSX with highlighting
  const highlightCharDiffForDisplay = (displayStr: string, compareStr: string, isRemoved: boolean): React.ReactNode => {
    const result: Array<{ type: 'different' | 'common'; char: string }> = [];
    let displayIdx = 0;
    let compareIdx = 0;
    
    while (displayIdx < displayStr.length) {
      if (compareIdx >= compareStr.length) {
        // No more compare chars - all remaining are different
        result.push({ type: 'different', char: displayStr[displayIdx] });
        displayIdx++;
      } else if (displayStr[displayIdx] === compareStr[compareIdx]) {
        // Chars match
        result.push({ type: 'common', char: displayStr[displayIdx] });
        displayIdx++;
        compareIdx++;
      } else {
        // Chars don't match - this char is different
        result.push({ type: 'different', char: displayStr[displayIdx] });
        displayIdx++;
        compareIdx++;
      }
    }
    
    return (
      <>
        {result.map((item, idx) => {
          if (item.type === 'common') {
            return <span key={idx}>{item.char}</span>;
          } else {
            return (
              <span
                key={idx}
                style={{
                  backgroundColor: isRemoved ? '#ffd7d9' : '#acf2bd',
                  color: isRemoved ? '#d73a49' : '#28a745',
                  fontWeight: 500
                }}
              >
                {item.char}
              </span>
            );
          }
        })}
      </>
    );
  };

  // Character-level diff for short strings (like "2025" vs "2026")
  const highlightCharDiff = (oldStr: string, newStr: string): Array<{ type: 'removed' | 'added' | 'common'; text: string }> => {
    const result: Array<{ type: 'removed' | 'added' | 'common'; text: string }> = [];
    let oldIdx = 0;
    let newIdx = 0;
    
    while (oldIdx < oldStr.length || newIdx < newStr.length) {
      if (oldIdx >= oldStr.length) {
        result.push({ type: 'added', text: newStr[newIdx] });
        newIdx++;
      } else if (newIdx >= newStr.length) {
        result.push({ type: 'removed', text: oldStr[oldIdx] });
        oldIdx++;
      } else if (oldStr[oldIdx] === newStr[newIdx]) {
        result.push({ type: 'common', text: oldStr[oldIdx] });
        oldIdx++;
        newIdx++;
      } else {
        // Find next matching character
        let foundMatch = false;
        for (let i = newIdx + 1; i < newStr.length; i++) {
          if (oldStr[oldIdx] === newStr[i]) {
            while (newIdx < i) {
              result.push({ type: 'added', text: newStr[newIdx] });
              newIdx++;
            }
            foundMatch = true;
            break;
          }
        }
        if (!foundMatch) {
          for (let i = oldIdx + 1; i < oldStr.length; i++) {
            if (newStr[newIdx] === oldStr[i]) {
              while (oldIdx < i) {
                result.push({ type: 'removed', text: oldStr[oldIdx] });
                oldIdx++;
              }
              foundMatch = true;
              break;
            }
          }
        }
        if (!foundMatch) {
          result.push({ type: 'removed', text: oldStr[oldIdx] });
          result.push({ type: 'added', text: newStr[newIdx] });
          oldIdx++;
          newIdx++;
        }
      }
    }
    
    return result;
  };

  // Helper function to normalize boolean strings to proper booleans
  const normalizeBooleanString = (value: any): any => {
    if (typeof value === 'string') {
      let trimmed = value.trim();
      // Remove surrounding quotes if present
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
          (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        trimmed = trimmed.slice(1, -1).trim();
      }
      const lowerTrimmed = trimmed.toLowerCase();
      if (lowerTrimmed === 'true' || lowerTrimmed === '1' || lowerTrimmed === 'yes') {
        return true;
      }
      if (lowerTrimmed === 'false' || lowerTrimmed === '0' || lowerTrimmed === 'no') {
        return false;
      }
    }
    return value;
  };

  // Function to format value for display
  const formatValue = (value: any) => {
    // Normalize boolean strings first
    const normalizedValue = normalizeBooleanString(value);

    // Pretty print JSON-like strings so diff view stays readable.
    // Handles direct JSON, quoted JSON strings and escaped JSON strings.
    if (typeof normalizedValue === 'string') {
      const trimmed = normalizedValue.trim();

      const tryPrettyPrintJson = (candidate: string): string | null => {
        const candidateTrimmed = candidate.trim();
        const looksLikeJson =
          (candidateTrimmed.startsWith('{') && candidateTrimmed.endsWith('}')) ||
          (candidateTrimmed.startsWith('[') && candidateTrimmed.endsWith(']'));

        if (!looksLikeJson) return null;

        try {
          const parsed = JSON.parse(candidateTrimmed);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return null;
        }
      };
      const tryExtractAndPrettyPrintJson = (candidate: string): string | null => {
        const trimmedCandidate = candidate.trim();
        const firstObjectStart = trimmedCandidate.indexOf('{');
        const lastObjectEnd = trimmedCandidate.lastIndexOf('}');
        const firstArrayStart = trimmedCandidate.indexOf('[');
        const lastArrayEnd = trimmedCandidate.lastIndexOf(']');

        const extractedCandidates: string[] = [];

        if (firstObjectStart !== -1 && lastObjectEnd > firstObjectStart) {
          extractedCandidates.push(trimmedCandidate.slice(firstObjectStart, lastObjectEnd + 1));
        }

        if (firstArrayStart !== -1 && lastArrayEnd > firstArrayStart) {
          extractedCandidates.push(trimmedCandidate.slice(firstArrayStart, lastArrayEnd + 1));
        }

        for (const extracted of extractedCandidates) {
          try {
            const parsed = JSON.parse(extracted);
            return JSON.stringify(parsed, null, 2);
          } catch {
            // try next extracted candidate
          }
        }

        return null;
      };

      const directPretty = tryPrettyPrintJson(trimmed);
      if (directPretty) {
        return directPretty;
      }
      const extractedDirectPretty = tryExtractAndPrettyPrintJson(trimmed);
      if (extractedDirectPretty) {
        return extractedDirectPretty;
      }

      // Try unwrapping one JSON-encoded string layer, then pretty print.
      try {
        const unwrapped = JSON.parse(trimmed);
        if (typeof unwrapped === 'string') {
          const nestedPretty = tryPrettyPrintJson(unwrapped);
          if (nestedPretty) {
            return nestedPretty;
          }
        }
      } catch {
        // Keep original string formatting if decoding fails.
      }

      // Fallback: handle non-standard escaped/quoted payloads manually.
      let candidate = trimmed;
      for (let i = 0; i < 3; i++) {
        const isDoubleQuoted = candidate.startsWith('"') && candidate.endsWith('"');
        const isSingleQuoted = candidate.startsWith("'") && candidate.endsWith("'");
        if (isDoubleQuoted || isSingleQuoted) {
          candidate = candidate.slice(1, -1).trim();
        }

        const unescapedCandidate = candidate
          .replace(/\\"/g, '"')
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\r')
          .replace(/\\t/g, '\t')
          .replace(/\\\\/g, '\\')
          .trim();

        const fallbackPretty = tryPrettyPrintJson(unescapedCandidate);
        if (fallbackPretty) {
          return fallbackPretty;
        }
        const extractedFallbackPretty = tryExtractAndPrettyPrintJson(unescapedCandidate);
        if (extractedFallbackPretty) {
          return extractedFallbackPretty;
        }

        candidate = unescapedCandidate;
      }
    }
    
    if (normalizedValue === null) return 'null';
    if (normalizedValue === undefined) return ''; // Return empty string instead of 'undefined'
    if (typeof normalizedValue === 'string') return `"${normalizedValue}"`;
    if (typeof normalizedValue === 'boolean') return normalizedValue.toString();
    if (typeof normalizedValue === 'number') return normalizedValue.toString();
    if (Array.isArray(normalizedValue)) {
      if (normalizedValue.length === 0) return '[]';
      return JSON.stringify(normalizedValue, null, 2);
    }
    if (typeof normalizedValue === 'object') {
      if (Object.keys(normalizedValue).length === 0) return '{}';
      return JSON.stringify(normalizedValue, null, 2);
    }
    return String(normalizedValue);
  };

  // Detect JSON-like string payloads (including quoted/escaped variants).
  // For such values we prefer pretty rendering over inline word diff.
  const isStructuredJsonString = (value: any): boolean => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;

    const candidates: string[] = [trimmed];
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === 'string' && parsed.trim()) {
        candidates.push(parsed.trim());
      }
    } catch {
      // no-op
    }

    for (const raw of candidates) {
      let candidate = raw;
      for (let i = 0; i < 2; i++) {
        const isDoubleQuoted = candidate.startsWith('"') && candidate.endsWith('"');
        const isSingleQuoted = candidate.startsWith("'") && candidate.endsWith("'");
        if (isDoubleQuoted || isSingleQuoted) {
          candidate = candidate.slice(1, -1).trim();
        }

        const unescaped = candidate
          .replace(/\\"/g, '"')
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\r')
          .replace(/\\t/g, '\t')
          .replace(/\\\\/g, '\\')
          .trim();

        const looksLikeJson =
          (unescaped.startsWith('{') && unescaped.endsWith('}')) ||
          (unescaped.startsWith('[') && unescaped.endsWith(']'));

        if (looksLikeJson) {
          return true;
        }

        candidate = unescaped;
      }
    }

    return false;
  };

  // Function to check if value is complex (array or object)
  const isComplexValue = (value: any) => {
    return (Array.isArray(value) && value.length > 0) || 
           (typeof value === 'object' && value !== null && Object.keys(value).length > 0);
  };

  // Function to render complex values (arrays and objects) with individual field editing
  const renderComplexValue = (field: string, value: any, changeType: string, color: string, isIncomingData: boolean = false, currentValue?: any, incomingValue?: any) => {
    const isCurrentlyEditing = editingFields.has(field) || (editable && isIncomingData);
    if (isCurrentlyEditing) {
      console.log(`Complex value is in editing mode for field: ${field}, tempEditValue:`, tempEditValues[field]);
    }
    // For red line (isIncomingData = false), always use the passed value (currentValue) - never use localIncomingData
    // For green line (isIncomingData = true), use localIncomingData if available (includes user edits)
    // IMPORTANT: When editing, red line should always show the original currentValue, not the edited value
    const displayValue = editable && isIncomingData && localIncomingData?.[field] !== undefined 
      ? localIncomingData[field] 
      : (isIncomingData ? value : (currentValue !== undefined ? currentValue : value));

    // If not editable, show read-only complex values
    if (!editable) {
      return (
        <Box sx={{ 
          color: color,
          wordBreak: 'break-all',
          minWidth: 0,
          flex: 1,
          padding: '8px',
          borderRadius: '3px',
          transition: 'all 0.15s ease',
          backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.05)' : 
                          changeType === 'removed' ? 'rgba(215, 58, 73, 0.05)' : 
                          'transparent',
          border: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.2)' : 
                 changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.2)' : 
                 '1px solid transparent'
        }}>
          <pre style={{ 
            margin: 0, 
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap'
          }}>
            {formatValue(displayValue)}
          </pre>
        </Box>
      );
    }

    if (isCurrentlyEditing && isIncomingData) {
      // Editing mode for complex values - only show TextField for incoming data (green line)
      // Red line (current data) should always show the original value
      const textField = (
        <TextField
          inputRef={(el) => {
            inputRefs.current[field] = el;
          }}
          value={tempEditValues[field] ?? formatValueForEdit(displayValue)}
          placeholder={`Edit ${field}...`}
          onChange={(e) => {
            setTempEditValues(prev => ({
              ...prev,
              [field]: e.target.value
            }));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              saveEditing(field);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditing(field);
            } else if (e.key === 'Tab') {
              e.preventDefault();
              saveEditing(field);
            }
          }}
          onBlur={(e) => {
            // Don't save if we just started editing (prevent immediate blur)
            if (recentlyStartedEditing.current.has(field)) {
              console.log('Ignoring blur for recently started editing field:', field);
              return;
            }
            
            // Only save if blur is not caused by clicking on the ignore button
            // Check if the related target is the ignore button
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (relatedTarget && (relatedTarget.closest('button') || relatedTarget.closest('[role="button"]'))) {
              // If clicking on a button, don't save yet - let the button handler manage it
              return;
            }
            
            // Add a small delay to prevent immediate save on focus
            setTimeout(() => {
              // Save for explicit editing or always-input incoming mode
              if ((editingFields.has(field) || (editable && isIncomingData)) && !recentlyStartedEditing.current.has(field)) {
                saveEditing(field);
              }
            }, 150);
          }}
          variant="standard"
          autoFocus
          multiline
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            '& .MuiInputBase-root': {
              color: color,
              fontSize: '0.8rem',
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
              padding: '8px',
              minHeight: 'auto',
              backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.05)' : 
                              changeType === 'removed' ? 'rgba(215, 58, 73, 0.05)' : 
                              'transparent',
              width: '100%',
              '&::before': {
                display: 'none' // Remove underline
              },
              '&::after': {
                display: 'none' // Remove focus underline
              },
              '&:hover': {
                backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.08)' : 
                                changeType === 'removed' ? 'rgba(215, 58, 73, 0.08)' : 
                                'rgba(0, 0, 0, 0.05)',
                '&::before': {
                  display: 'none'
                }
              }
            },
            '& .MuiInputBase-input': {
              color: color,
              padding: '0',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              overflow: 'visible',
              resize: 'none',
              width: '100%',
              '&::placeholder': {
                color: 'rgba(0, 0, 0, 0.3)',
                opacity: 1
              }
            },
            '&.Mui-focused': {
              '& .MuiInputBase-root': {
                backgroundColor: 'transparent'
              }
            }
          }}
        />
      );
      
      // For incoming data (green line), return the TextField directly
      return textField;
    } else {
      // Display mode for complex values - with ignore button for current data
      const valueBoxContent = (
        <Box sx={{ 
          color: color,
          wordBreak: 'break-all',
          minWidth: 0,
          flex: 1,
          padding: '8px',
          borderRadius: '3px',
          cursor: 'text',
          transition: 'all 0.15s ease',
          position: 'relative',
          backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.05)' : 
                          changeType === 'removed' ? 'rgba(215, 58, 73, 0.05)' : 
                          'transparent',
          border: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.2)' : 
                 changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.2)' : 
                 '1px solid transparent',
          '&:hover': {
            backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.08)' : 
                            changeType === 'removed' ? 'rgba(215, 58, 73, 0.08)' : 
                            'rgba(0, 0, 0, 0.05)',
            outline: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.3)' : 
                    changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.3)' : 
                    '1px solid rgba(0, 0, 0, 0.2)',
            transform: 'translateY(-1px)'
          },
          '&:active': {
            transform: 'translateY(0)',
            backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.12)' : 
                           changeType === 'removed' ? 'rgba(215, 58, 73, 0.12)' : 
                           'rgba(0, 0, 0, 0.08)'
          }
        }}
        onClick={(e) => {
          // Only allow editing for incoming data (green line), not for current data (red line)
          if (!isIncomingData) {
            e.stopPropagation();
            return; // Don't allow editing current data
          }
          e.stopPropagation();
          console.log('Complex value clicked, starting edit for field:', field);
          startEditing(field, displayValue);
        }}
        >
          <pre style={{ 
            margin: 0, 
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap'
          }}>
            {formatValue(displayValue)}
          </pre>
        </Box>
      );
      
      // For current data (red lines) with complex values, add ignore button
      if (editable && !isIncomingData && currentValue !== undefined) {
        return (
          <Box sx={{ 
            flex: 1,
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            gap: '4px',
            position: 'relative'
          }}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
              {valueBoxContent}
            </Box>
            <Tooltip title="Use Current Value (Ignore Incoming)">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the value box click
                  console.log('Ignore button clicked - using current value instead of incoming');
                  if (onDataChange) {
                    onDataChange(field, currentValue);
                  }
                  // Update local data
                  setLocalIncomingData(prev => ({
                    ...prev,
                    [field]: currentValue
                  }));
                }}
                sx={{ 
                  color: '#d73a49',
                  padding: '2px',
                  borderRadius: '3px',
                  transition: 'all 0.15s ease',
                  opacity: 0.7,
                  flexShrink: 0,
                  '&:hover': {
                    color: '#b71c1c',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    opacity: 1
                  }
                }}
              >
                <BlockIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
      
      // For new entries (added fields) with complex values, add ignore button
      if (editable && isIncomingData && changeType === 'added') {
        return (
          <Box sx={{ 
            flex: 1,
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            gap: '4px',
            position: 'relative'
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {valueBoxContent}
            </Box>
            <Tooltip title="Ignore This Field (Remove from Import)">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Ignore button clicked - removing new complex field from import:', field);
                  // Remove field from local incoming data
                  setLocalIncomingData(prev => {
                    const newData = { ...prev };
                    delete newData[field];
                    return newData;
                  });
                  // Call parent callback to remove the field
                  if (onDataChange) {
                    onDataChange(field, undefined);
                  }
                }}
                sx={{ 
                  color: '#d73a49',
                  padding: '2px',
                  borderRadius: '3px',
                  transition: 'all 0.15s ease',
                  opacity: 0.7,
                  flexShrink: 0,
                  '&:hover': {
                    color: '#b71c1c',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    opacity: 1
                  }
                }}
              >
                <BlockIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
      
      return valueBoxContent;
    }
  };

  // Function to render field value with Cursor AI-like editing capability
  const renderFieldValue = (field: string, value: any, isEditing: boolean, changeType: string, color: string, isIncomingData: boolean = false, currentValue?: any, incomingValue?: any) => {
    const isCurrentlyEditing = editingFields.has(field) || (editable && isIncomingData);
    // For non-editable mode (confirmation step), always use the passed value
    // For editable mode:
    // - If isIncomingData (green line), use localIncomingData if available (includes user edits)
    // - If !isIncomingData (red line), always use the passed value (currentValue) - don't use localIncomingData
    const displayValue = editable && isIncomingData && localIncomingData?.[field] !== undefined 
      ? localIncomingData[field] 
      : value;
    const shouldRenderAsMultilineJson = isStructuredJsonString(displayValue);
    
    // Check if this is a complex value (array or object)
    if (isComplexValue(displayValue)) {
      return renderComplexValue(field, displayValue, changeType, color, isIncomingData, currentValue, incomingValue);
    }
    
    // If not editable, show read-only values
    if (!editable) {
      // For modified string or number values, show highlighted diff
      if (changeType === 'modified') {
        // Convert numbers to strings for comparison
        const currentStr = typeof currentValue === 'number' ? String(currentValue) : (typeof currentValue === 'string' ? currentValue : null);
        const incomingStr = typeof incomingValue === 'number' ? String(incomingValue) : (typeof incomingValue === 'string' ? incomingValue : null);
        
        if (
          currentStr !== null &&
          incomingStr !== null &&
          currentStr !== incomingStr &&
          !isStructuredJsonString(currentStr) &&
          !isStructuredJsonString(incomingStr)
        ) {
          const isRemoved = !isIncomingData;
          // Always compare currentValue (old) with incomingValue (new)
          // isRemoved determines which parts to highlight
          return (
            <Box sx={{ 
              color: color,
              wordBreak: 'break-all',
              minWidth: 0,
              flex: 1,
              padding: '2px 4px',
              borderRadius: '3px',
              transition: 'all 0.15s ease'
            }}>
              {highlightStringDiff(currentStr, incomingStr, isRemoved)}
            </Box>
          );
        }
      }
      
      return (
        <Box sx={{ 
          color: color,
          wordBreak: 'break-all',
          minWidth: 0,
          flex: 1,
          padding: '2px 4px',
          borderRadius: '3px',
          transition: 'all 0.15s ease',
          whiteSpace: shouldRenderAsMultilineJson ? 'pre-wrap' : 'normal'
        }}>
          {formatValue(displayValue)}
        </Box>
      );
    }
    
    // For simple fields - show editable field
    if (isIncomingData || changeType === 'unchanged' || changeType === 'added') {
      return (
        <Box sx={{ 
          flex: 1,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          gap: '4px',
          position: 'relative'
        }}>
          {isCurrentlyEditing ? (
            // Editing mode - seamless inline editing (looks exactly like the text)
            <TextField
              inputRef={(el) => {
                inputRefs.current[field] = el;
              }}
              value={tempEditValues[field] ?? formatValueForEdit(displayValue)}
              placeholder={`Edit ${field}...`}
              onChange={(e) => {
                setTempEditValues(prev => ({
                  ...prev,
                  [field]: e.target.value
                }));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveEditing(field);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEditing(field);
                } else if (e.key === 'Tab') {
                  e.preventDefault();
                  saveEditing(field);
                }
              }}
              onBlur={() => {
                // Save immediately on blur (including always-input incoming mode)
                if (editingFields.has(field) || (editable && isIncomingData)) {
                  saveEditing(field);
                }
              }}
              variant="standard"
              autoFocus
              multiline
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiInputBase-root': {
                  color: color,
                  fontSize: '0.8rem',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                  padding: '2px 4px',
                  minHeight: 'auto',
                  height: 'auto',
                  backgroundColor: 'transparent',
                  '&::before': {
                    display: 'none' // Remove underline
                  },
                  '&::after': {
                    display: 'none' // Remove focus underline
                  },
                  '&:hover': {
                    '&::before': {
                      display: 'none'
                    }
                  }
                },
                '& .MuiInputBase-input': {
                  color: color,
                  padding: '0',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  overflow: 'hidden',
                  resize: 'none',
                  lineHeight: '2.2',
                  minHeight: '2.2em',
                  '&::placeholder': {
                    color: 'rgba(0, 0, 0, 0.3)',
                    opacity: 1
                  }
                },
                '&.Mui-focused': {
                  '& .MuiInputBase-root': {
                    backgroundColor: 'transparent'
                  }
                }
              }}
            />
          ) : (
            // Display mode - click to edit (Cursor AI style)
            <Box sx={{ 
              color: color,
              wordBreak: 'break-all',
              minWidth: 0,
              flex: 1,
              padding: '2px 4px',
              borderRadius: '3px',
              cursor: 'text',
              transition: 'all 0.15s ease',
              whiteSpace: shouldRenderAsMultilineJson ? 'pre-wrap' : 'normal',
              position: 'relative',
              '&:hover': {
                backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.08)' : 
                              changeType === 'removed' ? 'rgba(215, 58, 73, 0.08)' : 
                              'rgba(0, 0, 0, 0.05)',
                outline: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.3)' : 
                        changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.3)' : 
                        '1px solid rgba(0, 0, 0, 0.2)',
              },
              '&:active': {
                backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.12)' : 
                               changeType === 'removed' ? 'rgba(215, 58, 73, 0.12)' : 
                               'rgba(0, 0, 0, 0.08)'
              }
            }}
            onClick={(event) => {
              const clickCursorPos = getCursorPositionFromClick(event, String(formatValue(displayValue)));
              startEditing(field, displayValue, clickCursorPos);
            }}
            >
              {formatValue(displayValue)}
            </Box>
          )}
          {/* Ignore button for new entries (added fields) - remove from incoming data */}
          {editable && changeType === 'added' && (
            <Tooltip title="Ignore This Field (Remove from Import)">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Ignore button clicked - removing new field from import:', field);
                  // Remove field from local incoming data (set to undefined)
                  setLocalIncomingData(prev => {
                    const newData = { ...prev };
                    delete newData[field];
                    return newData;
                  });
                  // Call parent callback to remove the field
                  if (onDataChange) {
                    onDataChange(field, undefined);
                  }
                }}
                sx={{ 
                  color: '#d73a49',
                  padding: '2px',
                  borderRadius: '3px',
                  transition: 'all 0.15s ease',
                  opacity: 0.7,
                  flexShrink: 0,
                  '&:hover': {
                    color: '#b71c1c',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    opacity: 1
                  }
                }}
              >
                <BlockIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }

    // For current data (red lines) - show value with ignore button
    return (
      <Box sx={{ 
        flex: 1,
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        gap: '4px'
      }}>
        {!editable ? (
          // Read-only mode
          <Box sx={{ 
            color: color,
            wordBreak: 'break-all',
            minWidth: 0,
            flex: 1,
            padding: isComplexValue(value) ? '8px' : '2px 4px',
            borderRadius: '3px',
            transition: 'all 0.15s ease',
            backgroundColor: isComplexValue(value) ? (changeType === 'removed' ? 'rgba(215, 58, 73, 0.05)' : 'transparent') : 'transparent',
            border: isComplexValue(value) ? (changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.2)' : '1px solid transparent') : 'none'
          }}>
            {isComplexValue(value) ? (
              <pre style={{ 
                margin: 0, 
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                fontSize: '0.8rem',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap'
              }}>
                {formatValue(value)}
              </pre>
            ) : changeType === 'modified' ? (
              // Show highlighted diff for modified strings or numbers in removed line
              (() => {
                const valueStr = typeof value === 'number' ? String(value) : (typeof value === 'string' ? value : null);
                const incomingStr = typeof incomingValue === 'number' ? String(incomingValue) : (typeof incomingValue === 'string' ? incomingValue : null);
                if (
                  valueStr !== null &&
                  incomingStr !== null &&
                  valueStr !== incomingStr &&
                  !isStructuredJsonString(valueStr) &&
                  !isStructuredJsonString(incomingStr)
                ) {
                  return highlightStringDiff(valueStr, incomingStr, true);
                }
                return formatValue(value);
              })()
            ) : (
              formatValue(value)
            )}
          </Box>
        ) : isComplexValue(value) ? (
          <Box sx={{ 
            color: color,
            wordBreak: 'break-all',
            minWidth: 0,
            flex: 1,
            padding: '8px',
            borderRadius: '3px',
            transition: 'all 0.15s ease',
            cursor: 'default',
            backgroundColor: 'rgba(215, 58, 73, 0.05)',
            border: '1px solid rgba(215, 58, 73, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(215, 58, 73, 0.08)',
              outline: '1px solid rgba(215, 58, 73, 0.3)'
            }
          }}
          >
            <pre style={{ 
              margin: 0, 
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
              fontSize: '0.8rem',
              lineHeight: '1.4',
              whiteSpace: 'pre-wrap'
            }}>
              {formatValue(value)}
            </pre>
          </Box>
        ) : (
          <Box sx={{ 
            color: color,
            wordBreak: 'break-all',
            minWidth: 0,
            flex: 1,
            padding: '2px 4px',
            borderRadius: '3px',
            transition: 'all 0.15s ease',
            cursor: 'default',
            '&:hover': {
              backgroundColor: 'rgba(215, 58, 73, 0.08)',
              outline: '1px solid rgba(215, 58, 73, 0.3)'
            }
          }}
          >
            {formatValue(value)}
          </Box>
        )}
        {editable && currentValue !== undefined && (
          <Tooltip title="Use Current Value (Ignore Incoming)">
            <IconButton 
              size="small" 
              onClick={() => {
                console.log('Ignore button clicked - using current value instead of incoming');
                if (onDataChange) {
                  onDataChange(field, currentValue);
                }
                // Update local data
                setLocalIncomingData(prev => ({
                  ...prev,
                  [field]: currentValue
                }));
              }}
              sx={{ 
                color: '#d73a49',
                padding: '2px',
                borderRadius: '3px',
                transition: 'all 0.15s ease',
                opacity: 0.7,
                '&:hover': {
                  color: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  opacity: 1
                }
              }}
            >
              <BlockIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Function to render a single line with GitHub-style diff
  const renderDiffLine = (field: string, currentValue: any, incomingValue: any, lineNumber: number) => {
    const changeType = getChangeType(currentValue, incomingValue);
    
    // Handle unchanged fields first - show in grey if not edited
    if (changeType === 'unchanged' && !isFieldEdited(field)) {
      const lineStyle = {
        backgroundColor: '#f6f8fa',
        borderLeft: '4px solid #d0d7de',
        color: '#656d76',
        '&:hover': {
          backgroundColor: '#eaeef2',
          boxShadow: 'inset 0 0 0 1px rgba(208, 215, 222, 0.3)',
        }
      };
      
      return (
        <Box 
          key={field}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '28px',
            transition: 'all 0.15s ease',
            ...lineStyle
          }}
        >
          {/* Line number */}
          <Box sx={{ 
            width: '50px',
            padding: '0 8px',
            textAlign: 'right',
            color: '#656d76',
            backgroundColor: '#f6f8fa',
            borderRight: '1px solid #d0d7de',
            flexShrink: 0,
            userSelect: 'none',
            fontSize: '0.75rem'
          }}>
            {String(lineNumber).padStart(3, ' ')}
          </Box>
          
          {/* Change indicator - empty for unchanged */}
          <Box sx={{ 
            width: '24px',
            padding: '0 4px',
            textAlign: 'center',
            flexShrink: 0,
            fontWeight: 'bold',
            fontSize: '1rem',
            color: '#656d76',
            backgroundColor: '#f6f8fa'
          }}>
            {' '}
          </Box>
          
          {/* Field name and value */}
          <Box sx={{ 
            flex: 1,
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0
          }}>
            <Box sx={{ 
              fontWeight: '500',
              color: '#656d76',
              marginRight: '8px',
              flexShrink: 0,
              fontSize: '0.8rem'
            }}>
              {field}:
            </Box>
            {renderFieldValue(field, incomingValue || currentValue, true, changeType, '#656d76', true, currentValue, incomingValue)}
          </Box>
        </Box>
      );
    }
    
    // For modified fields, render as two separate lines (removed + added)
    if (changeType === 'modified') {
      return (
        <Box key={field}>
          {/* Removed line */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '28px',
            backgroundColor: '#ffeef0',
            borderLeft: '4px solid #d73a49',
            color: '#24292e',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: '#ffebee',
              boxShadow: 'inset 0 0 0 1px rgba(215, 58, 73, 0.2)',
            }
          }}>
            {/* Line number */}
            <Box sx={{ 
              width: '50px',
              padding: '0 8px',
              textAlign: 'right',
              color: '#656d76',
              backgroundColor: '#f6f8fa',
              borderRight: '1px solid #d0d7de',
              flexShrink: 0,
              userSelect: 'none',
              fontSize: '0.75rem'
            }}>
              {String(lineNumber).padStart(3, ' ')}
            </Box>
            
            {/* Change indicator */}
            <Box sx={{ 
              width: '24px',
              padding: '0 4px',
              textAlign: 'center',
              flexShrink: 0,
              fontWeight: 'bold',
              fontSize: '1rem',
              color: '#d73a49',
              backgroundColor: '#ffeef0'
            }}>
              -
            </Box>
            
            {/* Field name and value */}
            <Box sx={{ 
              flex: 1,
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              minWidth: 0
            }}>
              <Box sx={{ 
                fontWeight: '500',
                color: '#24292e',
                marginRight: '8px',
                flexShrink: 0,
                fontSize: '0.8rem'
              }}>
                {field}:
              </Box>
              {renderFieldValue(field, currentValue, true, changeType, '#d73a49', false, currentValue, incomingValue)}
            </Box>
          </Box>
          
          {/* Added line */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '28px',
            backgroundColor: '#e6ffed',
            borderLeft: '4px solid #28a745',
            color: '#24292e',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: '#f0fff4',
              boxShadow: 'inset 0 0 0 1px rgba(40, 167, 69, 0.2)',
            }
          }}>
            {/* Line number */}
            <Box sx={{ 
              width: '50px',
              padding: '0 8px',
              textAlign: 'right',
              color: '#656d76',
              backgroundColor: '#f6f8fa',
              borderRight: '1px solid #d0d7de',
              flexShrink: 0,
              userSelect: 'none',
              fontSize: '0.75rem'
            }}>
              {String(lineNumber + 1).padStart(3, ' ')}
            </Box>
            
        {/* Change indicator */}
        <Box sx={{ 
          width: '24px',
          padding: '0 4px',
          textAlign: 'center',
          flexShrink: 0,
          fontWeight: 'bold',
          fontSize: '1rem',
          color: '#28a745',
          backgroundColor: '#e6ffed'
        }}>
          +
        </Box>
            
            {/* Field name and value */}
            <Box sx={{ 
              flex: 1,
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              minWidth: 0
            }}>
              <Box sx={{ 
                fontWeight: '500',
                color: '#24292e',
                marginRight: '8px',
                flexShrink: 0,
                fontSize: '0.8rem'
              }}>
                {field}:
              </Box>
              {renderFieldValue(field, incomingValue, true, changeType, '#28a745', true, currentValue, incomingValue)}
            </Box>
          </Box>
        </Box>
      );
    }
    
    // For added or unchanged fields
    const getLineStyle = () => {
      // Check if field was edited by user
      const wasEdited = isFieldEdited(field);
      
      // If unchanged and not edited, show in grey
      if (changeType === 'unchanged' && !wasEdited) {
        return {
          backgroundColor: '#f6f8fa',
          borderLeft: '4px solid #d0d7de',
          color: '#656d76',
          '&:hover': {
            backgroundColor: '#eaeef2',
            boxShadow: 'inset 0 0 0 1px rgba(208, 215, 222, 0.3)',
          }
        };
      }
      
      // For added or edited fields, show green
      return { 
        backgroundColor: '#e6ffed', 
        borderLeft: '4px solid #28a745',
        color: '#24292e',
        '&:hover': {
          backgroundColor: '#f0fff4',
          boxShadow: 'inset 0 0 0 1px rgba(40, 167, 69, 0.2)',
        }
      };
    };
    
    // Handle unchanged fields - show in grey
    if (changeType === 'unchanged' && !isFieldEdited(field)) {
      const lineStyle = {
        backgroundColor: '#f6f8fa',
        borderLeft: '4px solid #d0d7de',
        color: '#656d76',
        '&:hover': {
          backgroundColor: '#eaeef2',
          boxShadow: 'inset 0 0 0 1px rgba(208, 215, 222, 0.3)',
        }
      };
      
      return (
        <Box 
          key={field}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '28px',
            transition: 'all 0.15s ease',
            ...lineStyle
          }}
        >
          {/* Line number */}
          <Box sx={{ 
            width: '50px',
            padding: '0 8px',
            textAlign: 'right',
            color: '#656d76',
            backgroundColor: '#f6f8fa',
            borderRight: '1px solid #d0d7de',
            flexShrink: 0,
            userSelect: 'none',
            fontSize: '0.75rem'
          }}>
            {String(lineNumber).padStart(3, ' ')}
          </Box>
          
          {/* Change indicator - empty for unchanged */}
          <Box sx={{ 
            width: '24px',
            padding: '0 4px',
            textAlign: 'center',
            flexShrink: 0,
            fontWeight: 'bold',
            fontSize: '1rem',
            color: '#656d76',
            backgroundColor: '#f6f8fa'
          }}>
            {' '}
          </Box>
          
          {/* Field name and value */}
          <Box sx={{ 
            flex: 1,
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0
          }}>
            <Box sx={{ 
              fontWeight: '500',
              color: '#656d76',
              marginRight: '8px',
              flexShrink: 0,
              fontSize: '0.8rem'
            }}>
              {field}:
            </Box>
            {renderFieldValue(field, incomingValue || currentValue, true, changeType, '#656d76', true, currentValue, incomingValue)}
          </Box>
        </Box>
      );
    }

    const lineStyle = getLineStyle();
    const paddedLineNumber = String(lineNumber).padStart(3, ' ');

    return (
      <Box 
        key={field}
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          minHeight: '28px',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '0.8rem',
          lineHeight: '28px',
          transition: 'all 0.15s ease',
          ...lineStyle
        }}
      >
        {/* Line number */}
        <Box sx={{ 
          width: '50px',
          padding: '0 8px',
          textAlign: 'right',
          color: '#656d76',
          backgroundColor: '#f6f8fa',
          borderRight: '1px solid #d0d7de',
          flexShrink: 0,
          userSelect: 'none',
          fontSize: '0.75rem'
        }}>
          {paddedLineNumber}
        </Box>
        
        {/* Change indicator - GitHub style */}
        <Box sx={{ 
          width: '24px',
          padding: '0 4px',
          textAlign: 'center',
          flexShrink: 0,
          fontWeight: 'bold',
          fontSize: '1rem',
          color: '#28a745',
          backgroundColor: '#e6ffed'
        }}>
          +
        </Box>
        
        {/* Field name and value */}
        <Box sx={{ 
          flex: 1,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          minWidth: 0
        }}>
          <Box sx={{ 
            fontWeight: '500',
            color: '#24292e',
            marginRight: '8px',
            flexShrink: 0,
            fontSize: '0.8rem'
          }}>
            {field}:
          </Box>
          {renderFieldValue(
            field, 
            changeType === 'added' ? incomingValue : currentValue, 
            true, 
            changeType, 
            changeType === 'added' ? '#28a745' : '#24292e',
            true, // All fields should be editable
            currentValue,
            incomingValue
          )}
        </Box>
      </Box>
    );
  };

  // Get unified field order (using normalized data)
  const unifiedFields = createUnifiedFieldOrder(normalizedCurrentData, normalizedIncomingData);
  
  // Filter fields based on mode:
  // - In editable mode: show ALL fields (so user can edit any field), unless showOnlyConflicts is true
  // - In read-only mode: show only new or modified fields (for final confirmation view)
  // - If showOnlyConflicts is true: only show conflicting fields (added or modified), exclude unchanged
  const filteredFields = (editable && !showOnlyConflicts)
    ? unifiedFields // Show all fields in editable mode (unless filtering conflicts)
    : unifiedFields.filter(field => {
        // In read-only mode or when showing only conflicts, only show fields that are new or modified
        const currentValue = normalizedCurrentData?.[field];
        // Use localIncomingData if available (includes user edits), otherwise use normalizedIncomingData
        const incomingValue = localIncomingData?.[field] !== undefined ? localIncomingData[field] : normalizedIncomingData?.[field];
        const changeType = getChangeType(currentValue, incomingValue);
        
        // Only show fields that are added or modified (not unchanged or empty)
        return changeType === 'added' || changeType === 'modified';
      });
  
  // Helper to check if field was edited by user (different from original)
  const isFieldEdited = (field: string): boolean => {
    if (!editable) return false;
    const originalValue = normalizedIncomingData?.[field];
    const currentValue = localIncomingData?.[field];
    if (currentValue === undefined) return false;
    return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
  };

  // If no data, show a test case with sample data
  if (!currentData && !incomingData) {
    const testCurrentData = {
      title: "Old Title",
      vehicle_make: "Toyota",
      vehicle_year: 2023,
      removed_field: "This will be removed",
      eligible_regions: ["US", "CA"],
      finance_terms: {
        interest_rate: 3.5,
        term_months: 60
      },
      features: ["GPS", "Bluetooth", "Backup Camera"]
    };
    
    const testIncomingData = {
      title: "New Title",
      vehicle_make: "Honda", 
      vehicle_year: 2024,
      new_field: "Added Value",
      eligible_regions: ["US", "CA", "MX"],
      finance_terms: {
        interest_rate: 2.9,
        term_months: 72,
        down_payment: 5000
      },
      features: ["GPS", "Bluetooth", "Backup Camera", "Lane Assist"]
    };
    
    return (
      <Box sx={{ 
        border: '1px solid #d0d7de',
        borderRadius: '6px',
        overflow: 'hidden',
        mb: 2,
        backgroundColor: '#ffffff'
      }}>
        <Box sx={{ 
          backgroundColor: '#f6f8fa',
          padding: '8px 16px',
          borderBottom: '1px solid #d0d7de',
          fontSize: '0.8rem',
          fontWeight: '600',
          color: '#24292e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box>
            Test Record - Sample Data
          </Box>
          <Box sx={{ 
            fontSize: '0.75rem',
            color: '#656d76',
            fontWeight: '400'
          }}>
            Updated Record
          </Box>
        </Box>
        
        {/* Test diff content */}
        <Box sx={{ 
          backgroundColor: '#ffffff',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '0.8rem',
          lineHeight: '28px'
        }}>
          {Object.keys({...testCurrentData, ...testIncomingData}).sort().map((field, index) => 
            renderDiffLine(
              field, 
              testCurrentData[field], 
              testIncomingData[field],
              index + 1
            )
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      TransitionProps={{ timeout: 0 }}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid #d0d7de',
        borderRadius: '6px',
        mb: 2,
        backgroundColor: '#ffffff',
        '&:before': {
          display: 'none'
        },
        '& .MuiAccordionSummary-root': {
          minHeight: 'unset'
        },
        '& .MuiAccordionSummary-content': {
          margin: 0
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#656d76' }} />}
        sx={{
          backgroundColor: '#f6f8fa',
          px: 2,
          py: 1,
          borderBottom: isExpanded ? '1px solid #d0d7de' : 'none',
          position: 'sticky',
          top: '49px',
          zIndex: 6
        }}
      >
        <Box
          sx={{
            width: '100%',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#24292e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1
          }}
        >
          <Box>
            {recordType} {recordIndex + 1}
          </Box>
          <Box
            sx={{
              fontSize: '0.75rem',
              color: '#656d76',
              fontWeight: 400
            }}
          >
            {!normalizedCurrentData ? 'New Record' : 'Updated Record'}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {/* Diff Content - GitHub style */}
        <Box
          sx={{
            backgroundColor: '#ffffff',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '28px'
          }}
        >
          {filteredFields.map((field, index) => {
            // Always use original normalizedIncomingData for comparison in renderDiffLine
            // The renderFieldValue function will handle using localIncomingData for display when needed
            return renderDiffLine(
              field,
              normalizedCurrentData?.[field],
              normalizedIncomingData?.[field],
              index + 1
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default JsonDiffViewer;